/* Password -> AES key. Salt and iterations are public; the key is derived in
   the browser from what the visitor types and is never part of the code. */
import params from "./params.json";
export const KEY_STORAGE = "portfolio-key";

export function hexToBytes(hex: string) {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}
function bytesToHex(bytes: ArrayBuffer) {
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function importAesKey(raw: Uint8Array) {
  return crypto.subtle.importKey("raw", raw, "AES-GCM", false, ["decrypt"]);
}

export async function decrypt(key: CryptoKey, payload: Uint8Array) {
  const iv = payload.slice(0, 12);
  const data = payload.slice(12);
  return new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, data));
}

/** False when the browser has no Web Crypto (pages not served over HTTPS). */
export function canUnlockHere(): boolean {
  return typeof crypto !== "undefined" && !!crypto.subtle;
}

/** Returns true and remembers the key for this tab if the password is right. */
export async function unlockWithPassword(password: string): Promise<boolean> {
  try {
    const material = await crypto.subtle.importKey("raw", new TextEncoder().encode(password), "PBKDF2", false, ["deriveBits"]);
    const bits = await crypto.subtle.deriveBits(
      { name: "PBKDF2", hash: "SHA-256", salt: hexToBytes(params.salt), iterations: params.iterations },
      material,
      256,
    );
    // Only the right password gives a key whose SHA-256 matches the check.
    // The values live in params.json so this works in any build, including
    // editor previews that do not use this repo's vite.config.
    const digest = bytesToHex(await crypto.subtle.digest("SHA-256", bits));
    if (digest !== params.check) return false;
    sessionStorage.setItem(KEY_STORAGE, bytesToHex(bits));
    return true;
  } catch {
    return false;
  }
}

export function hasStoredKey() {
  try {
    return !!sessionStorage.getItem(KEY_STORAGE);
  } catch {
    return false;
  }
}

export async function storedKey() {
  const hex = sessionStorage.getItem(KEY_STORAGE);
  if (!hex) throw new Error("locked");
  return importAesKey(hexToBytes(hex));
}
